import React, { forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, Typography, Box } from '@mui/material';
import { ListItem, ListItemButton } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import SubmitButton from 'src/components/formik-helper';
import DateAndTimePicker from 'src/components/widgets/date-time-picker';
import DropdownTracking from 'src/components/dropdown/dropdown-tracking';
import SearchSelectField from 'src/components/widgets/search-select-field';

import CustomRowList from 'src/components/ui/list/custom-row-list';
import HeaderCellText from 'src/components/ui/list/header-cell-text';
import ListCellText from 'src/components/ui/list/list-cell-text';
import { useTrackingLogic } from 'src/hooks/myhooks/use-tracking-logic';

import QuillEditor from 'src/components/quill-editor'

export const TrackingView = forwardRef((props, ref) => {
  const logic = useTrackingLogic(props);
  const item = props.item;

  useImperativeHandle(ref, () => ({
    reloadItem: (newItem) => {
      logic.resetFields();

      logic.setTrackingList(prev => {
        const id = newItem?.id;
        let updated = false;
        const newList = prev.map(item => {
          if (id && item.id === id) {
            updated = true;
            return { ...item, ...newItem };
          }
          return item;
        });
        return updated ? newList : prev;
      });
      
    },
    addTracking: (newTrack) => {
      logic.resetFields();

      logic.setTrackingList(prev => {
        // Skip duplicate check if mapType === 1
        if (newTrack.mapType === 1) {
          return [newTrack, ...prev];
        }
        const isDuplicate = prev.some(t => (t.id === newTrack.id || t.optionId === newTrack.optionId)
          && (t.statusId === newTrack.statusId));
        if (isDuplicate) return prev;
        return [newTrack, ...prev];
      });
    },
    removeTracking: (target) => {
      logic.resetFields();

      const id = target?.id;
      const trackingId = target?.trackingId;
  
      logic.setTrackingList(prev =>
        prev.filter(item => !(item.id === id && item.optionId === trackingId))
      );
    }
  }));

  const renderItem = (item, rowSx, idx) => (
    <ListItem
      divider={true}
      key={idx}
      disablePadding
    >
      <ListItemButton
        selected={item.optionId === logic.viewSelectedOptionId}
        onClick={() => logic.handleItemClicked(item)}
        sx={{
          ...rowSx,
          px: 1,
          py: 1,
          ...(item.optionId === logic.viewSelectedOptionId && {
            bgcolor: 'primary.light',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: 0,
              height: '100%',
              width: '4px',
              backgroundColor: 'primary.main',
            },
          }),
        }}
      >
        <ListCellText flex="0 0 5%" color='primary.main' primary={item.trackingStep} />
        <ListCellText flex="0 0 35%" primary={item.trackingName} />
        <ListCellText flex="0 0 20%" primary={item.statusName} />
        <ListCellText flex="0 0 20%" color='text.secondary' align='right' primary={item.remindDate} />
        <ListCellText flex="0 0 20%" color='text.secondary' align='right' primary={item.updatedDate} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <Card>
      <CardContent sx={{ px: 1, py: 1 }}>
        <Grid container spacing={2}>
          <Grid xs={12} md={4}>
            <form onSubmit={logic.formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid xs={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" color="primary">
                      {item.name}
                    </Typography>
                    <Typography variant="body2">
                      {item.birthday}
                    </Typography>
                  </Box>
                </Grid>
                <Grid xs={12}>
                  <DropdownTracking
                    ref={logic.dropdownRef}
                    label="Tiến độ đào tạo"
                    name="trackingField"
                    type="hv"
                    onSelected={logic.handleTrackingSelect}
                    hintValue={logic.element.tracking}
                  />
                </Grid>
                <Grid xs={12}>
                  <SearchSelectField
                    ref={logic.statusRef}
                    label="Trạng thái"
                    name="statusField"
                    options={logic.options}
                    onSelected={logic.handleStatusSelect}
                    // onSelected={(val) => {
                    //   logic.formik.setFieldValue('statusField', val?.value);

                    //   const isShow = val?.label === logic.element.status;
                    //   logic.setSubmitState(prev => ({
                    //     ...prev,
                    //     isShow,
                    //     isVerified: false
                    //   }));

                    //   logic.resetSubFields();
                    // }}
                    hintValue={logic.element.status}
                  />
                </Grid>
                <Grid xs={12}>
                  <DateAndTimePicker
                    ref={logic.pickerRef}
                    label="Ngày ghi chú"
                    name="remindField"
                    formik={logic.formik}
                    // hintValue={element.remindAt}
                    ignoreDefault={true}
                  />
                </Grid>

                <Grid xs={12}>
                  <DateAndTimePicker
                    ref={logic.pickerRef}
                    label="Ngày cập nhật"
                    name="updatedField"
                    formik={logic.formik}
                  />
                </Grid>

                <Grid xs={12} md={12} display="flex"
                  sx={{ justifyContent: { xs: 'center', md: 'flex-end' } }}
                >
                  <SubmitButton formik={logic.formik} isRemove={logic.submitState.isRemoved} 
                    labelSubmit={logic.submitState.isRemoved ? 'Cập nhật' : 'Thêm mới'}
                    disabled={!logic.submitState.isDisabled} 
                    hasVerified={logic.submitState.isVerified} 
                    onVerifyChange={(isChecked) => {
                      logic.setSubmitState(prev => ({
                        ...prev,
                        isVerified: isChecked
                      }));
                    }} />
                </Grid>

                <Grid xs={12}>
                  <QuillEditor
                    ref={logic.editorRef}
                    placeholder={'Điểm thi hoặc Ghi chú'}
                    value={logic.element.note}
                    sx={{ height: 92 }}
                  />
                </Grid>

              </Grid>
            </form>
          </Grid>

         <Grid xs={12} md={8}>
            <CustomRowList
              items={logic.trackingList} 
              renderItem={renderItem}
              sx={{
                height: '100%',
              }}
            >
              <HeaderCellText sx={{ width: '5%' }}>Stt</HeaderCellText>
              <HeaderCellText sx={{ width: '35%' }}>Tiến độ</HeaderCellText>
              <HeaderCellText sx={{ width: '20%' }}>Trạng Thái</HeaderCellText>
              <HeaderCellText sx={{ width: '20%', textAlign: 'right' }}>Ngày ghi chú</HeaderCellText>
              <HeaderCellText sx={{ width: '20%', textAlign: 'right' }}>Ngày cập nhật</HeaderCellText>
            </CustomRowList>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
});

TrackingView.propTypes = {
  item: PropTypes.object.isRequired,
};
